import { IPagination } from '@/types/general.type';
import { ApiResponse, axiosInstance } from '@/lib/axios';
import { queryOptions, useQuery } from '@tanstack/react-query';
import { QueryConfig } from '@/lib/react-query';
import { TFetchParams } from '@/types/fetch.type';
import { filterEmptyValues } from '@/utils/object';
import { TSpace } from '../../types/space.type';

export type TGetListSpaceAdminResponse = ApiResponse<IPagination<TSpace>>;
export type TGetListSpaceAdminParams = TFetchParams;

export const getListSpaceAdmin = async (params: TGetListSpaceAdminParams): Promise<TGetListSpaceAdminResponse> => {
  const url = '/space/admin';

  const filteredParams = filterEmptyValues(params);

  return axiosInstance.get(url, { params: filteredParams });
};

export const GET_LIST_SPACE_ADMIN_QUERY_KEY = 'get-list-space-admin';

export const getListSpaceAdminQueryOptions = <TData = TGetListSpaceAdminResponse>(
  params: TGetListSpaceAdminParams,
  select?: (data: TGetListSpaceAdminResponse) => TData
) => {
  return queryOptions({
    queryKey: [
      GET_LIST_SPACE_ADMIN_QUERY_KEY,
      {
        search: params?.search,
        page: params?.page,
        limit: params?.limit,
        sortDir: params?.sortDir,
        sortBy: params?.sortBy,
      },
    ],
    queryFn: () => getListSpaceAdmin(params),
    select,
  });
};

// Overload when select is provided - returns transformed type
export function useListSpaceAdminQuery<TData>(options: {
  params: TGetListSpaceAdminParams;
  queryConfig?: QueryConfig<typeof getListSpaceAdminQueryOptions<TData>>;
  select: (data: TGetListSpaceAdminResponse) => TData;
}): ReturnType<typeof useQuery<TData>>;

// Overload when select is not provided - returns raw API response (even if generic is provided)
export function useListSpaceAdminQuery(options: {
  params: TGetListSpaceAdminParams;
  queryConfig?: QueryConfig<typeof getListSpaceAdminQueryOptions<TGetListSpaceAdminResponse>>;
}): ReturnType<typeof useQuery<TGetListSpaceAdminResponse>>;

// Implementation
export function useListSpaceAdminQuery<TData = TGetListSpaceAdminResponse>({
  queryConfig,
  params,
  select,
}: {
  params: TGetListSpaceAdminParams;
  queryConfig?: QueryConfig<typeof getListSpaceAdminQueryOptions<TData | TGetListSpaceAdminResponse>>;
  select?: (data: TGetListSpaceAdminResponse) => TData;
}) {
  return useQuery({
    ...getListSpaceAdminQueryOptions(params, select),
    ...queryConfig,
  }) as ReturnType<typeof useQuery<TData extends TGetListSpaceAdminResponse ? TGetListSpaceAdminResponse : TData>>;
}
